import { useEffect, useMemo, useState } from "react";
import { useMemoizedFn, useRequest } from "ahooks";
import { commonFilterOptions, CommonOptionsForm, DemandDetailsResDto, outsourceCommonFilterOptions } from "#services/api";
import { $sType } from "#utils";
import { noop } from "@bedrock/components/lib/utils";

const filterOptionsApiMap = {
  inner: commonFilterOptions,
  outer: outsourceCommonFilterOptions
}

/** 按 key + finalParams 分别缓存每个 key 的接口返回数据 */
const keyCacheMap: Record<string, { item: { key: string; name: string; options: any[] }; optionsList: any[] }> = {};

function getCacheKey(key: string, params: object) {
  return `${key}_${JSON.stringify(params)}`;
}

function getFromCache(keys: string[], params: object) {
  let cacheHit = false;
  const optionsMap: Record<string, { key: string; name: string; options: any[] }> = {};
  const optionsListMap: Record<string, any[]> = {};

  keys.forEach((key) => {
    const cacheKey = getCacheKey(key, params);
    const cached = keyCacheMap[cacheKey];
    if (cached) {
      optionsMap[key] = cached.item;
      optionsListMap[key] = cached.optionsList;
      cacheHit = true
    } else {
      optionsMap[key] = { key, name: '加载中', options: [] };
      optionsListMap[key] = [];
    }
  });
  return { optionsMap, optionsListMap, cacheHit };
}

function setCache(key: string, params: object, item: { key: string; name: string; options: any[] }) {
  const cacheKey = getCacheKey(key, params);
  keyCacheMap[cacheKey] = {
    item: { key: item.key, name: item.name, options: item.options || [] },
    optionsList: item.options || [],
  };
}

type Props = {
  keys: string[] // 需要获取的options的key
  demandDetail?: DemandDetailsResDto, // 需求单详情
  cache?: boolean, // 是否缓存
  params?: CommonOptionsForm, // 可以传递的额外参数
  manual?: boolean // 是否手动触发
  onSuccess?: ({
    optionsMap,
    optionsListMap,
  }: {
    optionsMap: OptionsMap,
    optionsListMap: OptionsListMap,
  }) => void // 成功回调
  retryCount?: number // 重试次数
}

type OptionsMap = Record<string, {
  key: string,
  name: string,
  options: any[]
}>

type OptionsListMap = Record<string, any[]>

export const useDemandOptionsWithCache = ({
  keys, // 需要获取的options的key
  demandDetail, // 需求单详情
  params, // 可以传递的额外参数
  cache = true,
  manual = false, // 手动
  onSuccess = noop, // 成功回调
  retryCount = 3
}: Props) => {

  const finalKeys = useMemo(() => {
    return params?.keys || keys || []
  }, [keys, params?.keys])

  const finalParams = useMemo(() => {
    const sendParams: CommonOptionsForm = {
      ...params,
    }

    if (demandDetail?.group_type?.toUpperCase?.() === 'CD_TRUMPET_GROUP') {
      // 小喇叭
      sendParams.group_type = sendParams.group_type || 'CD_TRUMPET_GROUP';
      demandDetail?.state && (sendParams.trumpet_state = sendParams.trumpet_state || (demandDetail.state.toUpperCase() as any));
      demandDetail?.id && (sendParams.trumpet_demand_id = sendParams.trumpet_demand_id || demandDetail.id);
    } else {
      // 常規需求单
      sendParams.group_type = sendParams.group_type || demandDetail?.group_type?.toUpperCase?.() as any || "CHANNEL_GROUP";
      demandDetail?.state && (sendParams.demand_state = sendParams.demand_state || (demandDetail.state.toUpperCase() as any));
      demandDetail?.id && (sendParams.demand_id = sendParams.demand_id || demandDetail.id);
    }
    return sendParams
  }, [finalKeys, JSON.stringify(params), demandDetail?.group_type, demandDetail?.state, demandDetail?.id])

  const initState = useMemo(() => {
    return getFromCache(finalKeys, finalParams);
  }, [finalKeys, finalParams, cache]);

  const [optionsMap, setOptionsMap] = useState<OptionsMap>(initState.optionsMap);
  const [optionsListMap, setOptionsListMap] = useState<OptionsListMap>(initState.optionsListMap);
  const [cacheHit, setCacheHit] = useState(initState.cacheHit);

  // 当 finalParams/finalKeys 变化时，优先从缓存恢复已有 key 的数据
  useEffect(() => {
    if (!cache || finalKeys.length === 0) return;
    const { optionsMap: cachedMap, optionsListMap: cachedListMap, cacheHit } = getFromCache(finalKeys, finalParams);
    setOptionsMap(cachedMap);
    setOptionsListMap(cachedListMap);
    setCacheHit(cacheHit);
  }, [JSON.stringify(finalKeys), JSON.stringify(finalParams), cache]);

  const { mutate, ...rest } = useRequest(
    () => {
      return filterOptionsApiMap[$sType]({ ...finalParams, keys: finalKeys }).then((res) => {
        if (res.code === 1) {
          return res.data;
        }
      });
    },
    {
      manual,
      retryCount,
      onSuccess: (data: any[]) => {
        if (!data) return;
        const newOptionsMap: OptionsMap = {};
        const newListMap: OptionsListMap = {};
        data.forEach((item) => {
          newOptionsMap[item.key] = item;
          newListMap[item.key] = item.options ?? [];
          if (cache) {
            setCache(item.key, finalParams, item);
          }
        });
        setOptionsMap(newOptionsMap);
        setOptionsListMap(newListMap);
        onSuccess({
          optionsMap: newOptionsMap,
          optionsListMap: newListMap,
        });
      },
    }
  );


  // useDeepCompareEffect(() => {
  //   if (manual && !triggerKey) return
  //   const sendParams: CommonOptionsForm = {
  //     keys,
  //     ...params,
  //   }

  //   if (demandDetail?.group_type?.toUpperCase?.() === 'CD_TRUMPET_GROUP') {
  //     // 小喇叭
  //     sendParams.group_type = sendParams.group_type || 'CD_TRUMPET_GROUP';
  //     demandDetail?.state && (sendParams.trumpet_state = sendParams.trumpet_state || (demandDetail.state.toUpperCase() as any));
  //     demandDetail?.id && (sendParams.trumpet_demand_id = sendParams.trumpet_demand_id || demandDetail.id);
  //   } else {
  //     // 常規需求单
  //     sendParams.group_type = sendParams.group_type || demandDetail?.group_type?.toUpperCase?.() as any || "CHANNEL_GROUP";
  //     demandDetail?.state && (sendParams.demand_state = sendParams.demand_state || (demandDetail.state.toUpperCase() as any));
  //     demandDetail?.id && (sendParams.demand_id = sendParams.demand_id || demandDetail.id);
  //   }
  //   cacheFilterOptions(sendParams, cache).then(data => {
  //     const dataMap = data?.reduce((acc, cur) => {
  //       acc[cur.key] = cur
  //       return acc
  //     }, {})
  //     syncOptionsMap(dataMap, true)
  //   })
  // }, [
  //   keys,
  //   demandDetail,
  //   cache,
  //   params,
  //   manual,
  //   triggerKey
  // ])


  const getOptionsMap = useMemoizedFn(() => {
    mutate()
  })

  const memo = useMemo(() => {
    return { optionsListMap, optionsMap, getOptionsMap,cacheHit, mutate, ...rest }
  }, [optionsListMap, optionsMap, getOptionsMap, cacheHit, mutate, rest])

  return memo
}

